"use client";

// Admin edit mode: when the page is loaded with ?tsedit=1 (from the
// /admin/pages preview iframe), CMS-managed regions become directly editable
// in the real design:
//   [data-cms-key]  — plain text (innerText posted on input/blur)
//   [data-cms-html] — rich text (innerHTML posted, formatting kept)
//   [data-cms-img]  — click to change the image URL (paste from the Media
//                     library); the new URL is applied live and posted
// Edits postMessage to the parent admin window and merge into the draft;
// nothing persists until the admin clicks Save. Inert on normal visits.
import { useEffect } from "react";

export function CmsEditMode() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("tsedit")) return;
    if (window.parent === window) return; // only inside the admin iframe

    // Edit scope keeps the header/footer editors (which preview a full page)
    // from touching page-body content, and vice-versa:
    //   scope=header → only the <header>;  scope=footer → only the <footer>;
    //   no scope     → everything EXCEPT the header/footer chrome.
    const scope = params.get("scope");
    const inScope = (el: HTMLElement) => {
      const inHeader = !!el.closest("header");
      const inFooter = !!el.closest("footer");
      if (scope === "header") return inHeader;
      if (scope === "footer") return inFooter;
      return !inHeader && !inFooter;
    };

    const post = (key: string | undefined, value: string) => {
      if (!key) return;
      window.parent.postMessage({ type: "cms-edit", key, value }, window.location.origin);
    };
    const outline = (el: HTMLElement) => {
      el.style.outline = "2px dashed rgba(68,121,115,.8)";
      el.style.outlineOffset = "4px";
    };

    // In-page popup replacing window.prompt (blocked/ugly in the admin iframe).
    // Resolves to the entered string, or null on Cancel/Escape/backdrop.
    function openPrompt(opts: {
      title: string;
      hint?: string;
      value?: string;
      multiline?: boolean;
    }): Promise<string | null> {
      return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.style.cssText =
          "position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;background:rgba(16,32,29,.55);padding:20px;font-family:system-ui,sans-serif;";
        const card = document.createElement("div");
        card.style.cssText =
          "width:100%;max-width:440px;background:#fff;border-radius:16px;padding:22px;box-shadow:0 24px 60px -20px rgba(0,0,0,.5);";
        const h = document.createElement("p");
        h.textContent = opts.title;
        h.style.cssText = "margin:0 0 4px;font:600 15px/1.3 system-ui,sans-serif;color:#16302b;";
        card.appendChild(h);
        if (opts.hint) {
          const hint = document.createElement("p");
          hint.textContent = opts.hint;
          hint.style.cssText = "margin:0 0 12px;font:400 12px/1.4 system-ui,sans-serif;color:#5b6b67;";
          card.appendChild(hint);
        }
        const field = document.createElement(opts.multiline ? "textarea" : "input") as
          HTMLInputElement | HTMLTextAreaElement;
        field.value = opts.value ?? "";
        if (opts.multiline) (field as HTMLTextAreaElement).rows = 3;
        field.style.cssText =
          "width:100%;box-sizing:border-box;margin-top:8px;padding:10px 12px;border:1px solid #d8e0de;border-radius:10px;font:400 14px/1.4 system-ui,sans-serif;color:#16302b;outline:none;resize:vertical;";
        field.addEventListener("focus", () => { field.style.borderColor = "#447973"; });
        card.appendChild(field);
        const row = document.createElement("div");
        row.style.cssText = "display:flex;justify-content:flex-end;gap:10px;margin-top:16px;";
        const cancel = document.createElement("button");
        cancel.type = "button";
        cancel.textContent = "Cancel";
        cancel.style.cssText =
          "padding:9px 16px;border:1px solid #d8e0de;background:#fff;border-radius:999px;font:600 13px system-ui,sans-serif;color:#5b6b67;cursor:pointer;";
        const save = document.createElement("button");
        save.type = "button";
        save.textContent = "Save";
        save.style.cssText =
          "padding:9px 18px;border:0;background:#447973;border-radius:999px;font:600 13px system-ui,sans-serif;color:#fcffff;cursor:pointer;";
        row.append(cancel, save);
        card.appendChild(row);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
        field.focus();
        field.select();

        const close = (val: string | null) => {
          overlay.remove();
          document.removeEventListener("keydown", onKey);
          resolve(val);
        };
        const onKey = (e: KeyboardEvent) => {
          if (e.key === "Escape") close(null);
          if (e.key === "Enter" && !opts.multiline) close(field.value);
        };
        document.addEventListener("keydown", onKey);
        overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(null); });
        cancel.addEventListener("click", () => close(null));
        save.addEventListener("click", () => close(field.value));
      });
    }

    // Some editable elements mount after load (tab panels, modals), so the
    // wiring runs once now and again on DOM changes; wired() de-dupes.
    const wired = (el: HTMLElement) => {
      if (el.dataset.tseditWired) return true;
      el.dataset.tseditWired = "1";
      return false;
    };

    // Text regions
    const wireText = () => document.querySelectorAll<HTMLElement>("[data-cms-key]").forEach((el) => {
      if (wired(el) || !inScope(el)) return;
      el.setAttribute("contenteditable", "plaintext-only");
      outline(el);
      el.style.cursor = "text";

      // Edit the SOURCE, not the rendered text. A field holding
      // "[managed office space solutions](/solutions/suites)" renders as the
      // words alone, so posting innerText threw the link away — and because
      // send was bound to blur, clicking into a paragraph and out of it was
      // enough to strip every link and bold it held. Adding a second link
      // therefore appeared to delete the first.
      //
      // data-cms-raw carries what is stored. Swapping it in on focus means
      // the editor sees and edits the markers themselves, and what is posted
      // back is the same shape as what was saved.
      const raw = el.dataset.cmsRaw;
      let showingRaw = false;
      const showRaw = () => {
        if (showingRaw || raw === undefined || raw === el.innerText) return;
        showingRaw = true;
        el.textContent = raw;
      };
      el.addEventListener("focus", showRaw);

      // Only post when the text actually changed. Without this a stray click
      // rewrites the field with whatever happened to be on screen.
      let lastSent: string | null = null;
      const send = () => {
        const value = el.innerText.trim();
        const baseline = lastSent ?? (raw !== undefined ? raw.trim() : null);
        if (baseline !== null && value === baseline) return;
        lastSent = value;
        post(el.dataset.cmsKey, value);
      };
      el.addEventListener("blur", send);
      el.addEventListener("input", send);
    });

    // Multi-line headlines: one editable element mapping visual lines to
    // several keys (data-cms-lines="hero.line1,hero.line2,hero.line3").
    const wireLines = () => document.querySelectorAll<HTMLElement>("[data-cms-lines]").forEach((el) => {
      if (wired(el) || !inScope(el)) return;
      el.setAttribute("contenteditable", "plaintext-only");
      outline(el);
      el.style.cursor = "text";
      const keys = (el.dataset.cmsLines ?? "").split(",");
      const send = () => {
        const lines = el.innerText.split("\n").map((t) => t.trim()).filter(Boolean);
        keys.forEach((k, i) => post(k.trim(), lines[i] ?? ""));
      };
      el.addEventListener("blur", send);
      el.addEventListener("input", send);
    });

    // Rich-text regions (keep HTML)
    const wireHtml = () => document.querySelectorAll<HTMLElement>("[data-cms-html]").forEach((el) => {
      if (wired(el) || !inScope(el)) return;
      el.setAttribute("contenteditable", "true");
      outline(el);
      el.style.cursor = "text";
      const send = () => post(el.dataset.cmsHtml, el.innerHTML.trim());
      el.addEventListener("blur", send);
      el.addEventListener("input", send);
    });

    // Images — a floating "Change image" button pinned to each managed image
    // (background hero images sit behind content, so direct clicks can't
    // reach them; a pinned button works for every case).
    const wireImgs = () => document.querySelectorAll<HTMLElement>("[data-cms-img]").forEach((el) => {
      if (wired(el) || !inScope(el)) return;
      const img = el as HTMLImageElement;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "🖼 Change image";
      btn.style.cssText =
        "position:absolute;z-index:9998;background:#447973;color:#fcffff;font:600 12px/1 sans-serif;padding:8px 12px;border:0;border-radius:999px;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.25);";
      const place = () => {
        const r = img.getBoundingClientRect();
        btn.style.top = `${window.scrollY + r.top + 12}px`;
        btn.style.left = `${window.scrollX + r.left + 12}px`;
        btn.style.display = r.width > 0 ? "block" : "none";
      };
      place();
      window.addEventListener("resize", place);
      window.addEventListener("scroll", () => requestAnimationFrame(place), { passive: true });
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = img.getAttribute("src") ?? "";
        const url = await openPrompt({
          title: "Change image",
          hint: "Upload in the Media library and paste the copied URL here.",
          value: current,
        });
        if (url === null || url.trim() === "" || url === current) return;
        img.src = url.trim(); // live preview
        post(img.dataset.cmsImg, url.trim());
      });
      document.body.appendChild(btn);

      // Resolution badge — shows the current image's natural pixel dimensions
      // so editors know what they're replacing / uploading against.
      const resBadge = document.createElement("span");
      resBadge.style.cssText =
        "position:absolute;z-index:9998;background:rgba(16,32,29,.85);color:#fcffff;font:600 10px/1.1 sans-serif;letter-spacing:.02em;padding:5px 8px;border-radius:6px;pointer-events:none;white-space:nowrap;";
      const setRes = () => {
        resBadge.textContent = img.naturalWidth
          ? `${img.naturalWidth} × ${img.naturalHeight} px`
          : "";
      };
      setRes();
      img.addEventListener("load", setRes);
      const placeRes = () => {
        const r = img.getBoundingClientRect();
        resBadge.style.top = `${window.scrollY + r.top + 46}px`;
        resBadge.style.left = `${window.scrollX + r.left + 12}px`;
        resBadge.style.display = r.width > 0 && img.naturalWidth ? "block" : "none";
      };
      placeRes();
      window.addEventListener("resize", placeRes);
      window.addEventListener("scroll", () => requestAnimationFrame(placeRes), { passive: true });
      img.addEventListener("load", placeRes);
      document.body.appendChild(resBadge);

      // Alt-text editor — saved under "<image key>.alt". Every managed image
      // gets an editable alt for SEO/accessibility.
      const altBtn = document.createElement("button");
      altBtn.type = "button";
      altBtn.textContent = "ALT";
      altBtn.title = "Edit this image's alt text (SEO / accessibility)";
      altBtn.style.cssText =
        "position:absolute;z-index:9998;background:#16302b;color:#fcffff;font:600 11px/1 sans-serif;letter-spacing:.06em;padding:8px 10px;border:0;border-radius:999px;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.25);";
      const placeAlt = () => {
        const r = img.getBoundingClientRect();
        altBtn.style.top = `${window.scrollY + r.top + 12}px`;
        altBtn.style.left = `${window.scrollX + r.left + 136}px`;
        altBtn.style.display = r.width > 0 ? "block" : "none";
      };
      placeAlt();
      window.addEventListener("resize", placeAlt);
      window.addEventListener("scroll", () => requestAnimationFrame(placeAlt), { passive: true });
      altBtn.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const alt = await openPrompt({
          title: "Alt text / description",
          hint: "Describe the image for SEO and screen readers.",
          value: img.getAttribute("alt") ?? "",
          multiline: true,
        });
        if (alt === null) return;
        img.alt = alt.trim();
        post(`${img.dataset.cmsImg}.alt`, alt.trim());
      });
      document.body.appendChild(altBtn);
    });

    const wireAll = () => {
      wireText();
      wireLines();
      wireHtml();
      wireImgs();
    };
    wireAll();
    // Late-mounted editable content (tab panels, accordions, modals).
    const observer = new MutationObserver(() => wireAll());
    observer.observe(document.body, { childList: true, subtree: true });

    // Editing banner so the mode is unmistakable.
    const bar = document.createElement("div");
    bar.textContent =
      "EDITING — click outlined text to edit, click outlined images to change them, then Save in the admin panel";
    bar.style.cssText =
      "position:fixed;bottom:0;left:0;right:0;z-index:9999;background:#447973;color:#fcffff;font:500 12px/1 sans-serif;letter-spacing:.08em;text-align:center;padding:10px;";
    document.body.appendChild(bar);

    // Keep clicks from following links while editing.
    const stop = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest("a");
      if (a) e.preventDefault();
    };
    document.addEventListener("click", stop, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("click", stop, true);
    };
  }, []);

  return null;
}
